import React, { useState, useRef } from 'react';
import axios, { AxiosError } from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useIncludeFunctionaryForm } from '../../contexts/IncludeFunctionaryFormProvider';

// Funções auxiliares
const formatPhone = (value: string): string => {
  value = value.replace(/\D/g, '');
  if (value.length > 2) {
    value = `(${value.substring(0, 2)}) ${value.substring(
      2,
      7,
    )}-${value.substring(7, 11)}`;
  }
  return value;
};

const cleanPhone = (value: string): string => {
  return '55' + value.replace(/\D/g, '');
};

// Componente da Primeira Etapa
const PrimeiraEtapaComponent = ({
  handleNomeChange,
  handleTelefoneChange,
  handleAvançarClick,
  nome,
  telefoneFormatado,
  loading,
}: {
  handleNomeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleTelefoneChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAvançarClick: () => void;
  nome: string;
  telefoneFormatado: string;
  loading: boolean;
}) => (
  <>
    <TextField
      fullWidth
      margin="normal"
      label="Nome Completo"
      variant="outlined"
      value={nome}
      onChange={handleNomeChange}
      required
    />

    <TextField
      fullWidth
      margin="normal"
      label="Telefone Celular (com DDD)"
      variant="outlined"
      value={telefoneFormatado}
      onChange={handleTelefoneChange}
      required
      placeholder="(XX) XXXXX-XXXX"
    />

    <Divider sx={{ my: 2 }} />

    <Button
      variant="contained"
      color="primary"
      fullWidth
      sx={{ mt: 2 }}
      onClick={handleAvançarClick}
      disabled={
        !nome ||
        !telefoneFormatado ||
        telefoneFormatado.replace(/\D/g, '').length < 11 ||
        loading
      }
    >
      {loading ? <CircularProgress size={24} /> : 'Avançar'}
    </Button>
  </>
);

// Componente da Segunda Etapa
const SegundaEtapaComponent = ({
  telefoneOriginalFormatado,
  telefoneConfirmacao,
  handleTelefoneConfirmacaoChange,
  codigo,
  handleCodigoChange,
  handleSubmitCodigo,
  onCaptchaVerify,
  captchaRef,
  handleVoltarClick,
  loading,
  recaptchaValido,
}: {
  telefoneOriginalFormatado: string;
  telefoneConfirmacao: string;
  handleTelefoneConfirmacaoChange: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  codigo: string;
  handleCodigoChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmitCodigo: () => void;
  onCaptchaVerify: (token: string | null) => void;
  captchaRef: React.RefObject<ReCAPTCHA>;
  handleVoltarClick: () => void;
  loading: boolean;
  recaptchaValido: boolean;
}) => (
  <>
    <Typography variant="body1" gutterBottom>
      Confirme o número de telefone e insira o código recebido:
    </Typography>

    <TextField
      fullWidth
      margin="normal"
      label="Confirmar Telefone Celular"
      variant="outlined"
      value={telefoneConfirmacao}
      onChange={handleTelefoneConfirmacaoChange}
      required
      placeholder="(XX) XXXXX-XXXX"
    />

    <TextField
      fullWidth
      margin="normal"
      label="Código de 6 dígitos"
      variant="outlined"
      value={codigo}
      onChange={handleCodigoChange}
      required
      inputProps={{ maxLength: 6 }}
    />

    <Box my={2}>
      <ReCAPTCHA
        ref={captchaRef}
        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
        onChange={onCaptchaVerify}
      />
    </Box>

    <Divider sx={{ my: 2 }} />

    <Box display="flex" gap={2}>
      <Button
        variant="outlined"
        color="primary"
        fullWidth
        sx={{ mt: 2 }}
        onClick={handleVoltarClick}
        disabled={loading}
      >
        Voltar
      </Button>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        sx={{ mt: 2 }}
        onClick={handleSubmitCodigo}
        disabled={
          telefoneConfirmacao !== telefoneOriginalFormatado ||
          codigo.length !== 6 ||
          /* !recaptchaValido || */
          loading
        }
      >
        {loading ? <CircularProgress size={24} /> : 'Validar Código'}
      </Button>
    </Box>
  </>
);

// Componente Principal
const ValidacaoCadastro: React.FC = () => {
  const navigate = useNavigate();
  const { register, handleSubmit, goToNextForm, errors, watch, setValue } =
    useIncludeFunctionaryForm();
  const [nome, setNome] = useState('');
  const [telefoneFormatado, setTelefoneFormatado] = useState('');
  const [telefoneConfirmacao, setTelefoneConfirmacao] = useState('');
  const [codigo, setCodigo] = useState('');
  const [step, setStep] = useState(1);
  const [recaptchaValido, setRecaptchaValido] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ texto: '', tipo: '' });
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  // Handlers
  const handleNomeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNome(event.target.value);
    setValue('inicial.nome', event.target.value);
  };

  const handleTelefoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTelefoneFormatado(formatPhone(event.target.value));
    setValue('inicial.celular', event.target.value);
  };

  const handleTelefoneConfirmacaoChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setTelefoneConfirmacao(formatPhone(event.target.value));
  };

  const handleCodigoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/\D/g, '');
    if (value.length <= 6) setCodigo(value);
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaValido(!!token);
  };

  const handleAvançarClick = async () => {
    if (!nome || !telefoneFormatado) return;

    setLoading(true);
    try {
      const phoneNumber = cleanPhone(telefoneFormatado);

      const response = await axios.post(
        'https://odontoauth.odontogroup.com.br/auth/register',
        {
          name: nome,
          phone: phoneNumber,
        },
      );

      // Adicione esta verificação
      if (response.status === 200 || response.status === 201) {
        setStep(2); // Avança para a segunda etapa
        setMensagem({
          texto:
            'Cadastro iniciado com sucesso! Verifique seu telefone para o código.',
          tipo: 'success',
        });
      } else {
        setMensagem({ texto: 'Erro no cadastro', tipo: 'error' });
      }
    } catch (err) {
      const error = err as AxiosError;
      const errorMessage =
        (error.response?.data as any)?.error ||
        'Erro ao cadastrar. Tente novamente.';
      setMensagem({ texto: errorMessage, tipo: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCodigo = async () => {
    /* if (
      telefoneConfirmacao !== telefoneFormatado ||
      codigo.length !== 6 ||
      !recaptchaValido
    )
      return; */

    setLoading(true);
    try {
      const phoneNumber = cleanPhone(telefoneConfirmacao);

      const response = await axios.post(
        'https://odontoauth.odontogroup.com.br/auth/verify',
        {
          phone: phoneNumber,
          code: codigo,
        },
      );

      if (response.data.status === 'success') {
        setMensagem({ texto: response.data.message, tipo: 'success' });
        handleSubmit(goToNextForm)();
      } else {
        setMensagem({
          texto: response.data.error || 'Código inválido',
          tipo: 'error',
        });
      }
    } catch (err) {
      const error = err as AxiosError;
      const errorMessage =
        (error.response?.data as any)?.error ||
        'Erro na verificação. Tente novamente.';
      setMensagem({ texto: errorMessage, tipo: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleVoltarClick = () => {
    setStep(1);
    setRecaptchaValido(false);
    recaptchaRef.current?.reset();
  };

  const handleCloseMensagem = () => {
    setMensagem({ texto: '', tipo: '' });
  };

  return (
    <Container>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Paper
          elevation={3}
          style={{ padding: '2rem', maxWidth: '600px', width: '100%' }}
        >
          <Typography variant="h5" component="h3" gutterBottom align="center">
            {step === 1 ? 'Cadastro Inicial' : 'Verificação de Código'}
          </Typography>

          {step === 1 ? (
            <PrimeiraEtapaComponent
              handleNomeChange={handleNomeChange}
              handleTelefoneChange={handleTelefoneChange}
              handleAvançarClick={handleAvançarClick}
              nome={nome}
              telefoneFormatado={telefoneFormatado}
              loading={loading}
            />
          ) : (
            <SegundaEtapaComponent
              telefoneOriginalFormatado={telefoneFormatado}
              telefoneConfirmacao={telefoneConfirmacao}
              handleTelefoneConfirmacaoChange={handleTelefoneConfirmacaoChange}
              codigo={codigo}
              handleCodigoChange={handleCodigoChange}
              handleSubmitCodigo={handleSubmitCodigo}
              onCaptchaVerify={handleRecaptchaChange}
              captchaRef={recaptchaRef}
              handleVoltarClick={handleVoltarClick}
              loading={loading}
              recaptchaValido={recaptchaValido}
            />
          )}
        </Paper>
      </Box>

      <Snackbar
        open={!!mensagem.texto}
        autoHideDuration={6000}
        onClose={handleCloseMensagem}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseMensagem}
          severity={mensagem.tipo === 'success' ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {mensagem.texto}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ValidacaoCadastro;
